import { Component } from 'react'
import { PaymentRequestButtonElement } from '@stripe/react-stripe-js'
import PaymentManager from 'services/PaymentManager'
import { analytics } from 'services/Analytics/Analytics'
import UserManager from 'services/UserManager'
import { logger } from 'services/logger'
import { updatePaymentRequest as updateRequest } from '../../stores/paymentStore/payment.utils'

const APPLE_PAY_COMPLETE_DELAY_MS = 1500

class StripePaymentRequestForm extends Component {
  constructor(props) {
    super(props)
    const initialState = { holdPaymentButton: false }
    if (!props.paymentRequestData) {
      this.state = { canMakePayment: false, ...initialState }
      return
    }

    const { paymentRequestData } = props
    this.state = { ...paymentRequestData, ...initialState }
    if (paymentRequestData.canMakePayment) {
      const { paymentRequest } = paymentRequestData
      this.setPaymentRequestHandlers(paymentRequest)
    }
  }

  setPaymentRequestHandlers(paymentRequest) {
    paymentRequest.on('paymentmethod', (e) => {
      this.addPaymentMethod(e)
    })
    paymentRequest.on('cancel', () => {
      analytics.track('Payment Aborted', {
        paymentMethod: this.props.paymentMethod,
      })
    })
  }

  addPaymentMethod({ complete, paymentMethod }) {
    analytics.track('Received Payment Method', {
      paymentMethod: this.props.paymentMethod,
    })
    let email = UserManager.getUserEmail()
    if (!email) {
      email = paymentMethod.billing_details.email
    }
    PaymentManager.addStripePaymentMethod(
      paymentMethod,
      email,
      this.state.paymentType
    )
      .then(() => {
        // TODO: consider charging before success, looks weird on card decline flow.
        complete('success')
        setTimeout(() => {
          this.props.action()
        }, APPLE_PAY_COMPLETE_DELAY_MS)
        analytics.track('Complete Order After Payment', {
          paymentMethod: this.props.paymentMethod,
        })
      })
      .catch((error) => {
        analytics.track('Payment Error', {
          paymentMethod: this.props.paymentMethod,
          error,
        })
        logger.error(' failed adding payment method', error)
      })
  }

  updatePaymentRequest() {
    // NOTICE: this function shouldn't be called when the PaymentRequest interface is active.
    // The interval can fire before checkout pricing resolves, when currency is still missing.
    if (!this.props.orderPriceSummary?.currency) {
      return
    }

    if (
      this.state.orderPriceSummary &&
      this.state.orderPriceSummary.totalPrice ===
        this.props.orderPriceSummary.totalPrice &&
      this.state.orderPriceSummary.currency ===
        this.props.orderPriceSummary.currency
    ) {
      // update is not necessary.
      return
    }

    const paymentRequestData = {
      paymentRequest: this.state.paymentRequest,
      orderPriceSummary: this.props.orderPriceSummary,
      currency: this.props.currency,
      displayItems: this.props.displayItems,
    }

    updateRequest(paymentRequestData)
    this.setState(paymentRequestData)
  }

  componentDidMount() {
    this.interval = setInterval(() => {
      this.updatePaymentRequest()
    }, 1000)
  }

  componentWillUnmount() {
    if (this.state.paymentRequest) {
      // Avoid calling event listeners from unmounted payment request forms.
      this.state.paymentRequest.removeAllListeners()
    }
    clearInterval(this.interval)
  }

  releasePaymentButton() {
    // Release the hold if validation failed so the button can be clicked again
    setTimeout(() => {
      this.setState({ holdPaymentButton: false })
    }, 1000)
  }

  handleClick() {
    if (this.state.holdPaymentButton) {
      return
    }

    this.setState({ holdPaymentButton: true })
    if (!this.props.validate()) {
      analytics.track('Pay Tapped Validation Failed', {
        paymentMethod: this.props.paymentMethod,
      })
      this.releasePaymentButton()
      return
    }

    analytics.track('Pay Tapped', { paymentMethod: this.props.paymentMethod })
    this.state.paymentRequest.show()
    this.releasePaymentButton()
  }

  render() {
    if (!this.state.paymentRequest) {
      // TODO: checkout sheet disable init without payment request.
      logger.error(
        'tried to render PaymentRequestForm without a payment request'
      )
      return null
    }

    // Cypress test override: Stripe's PaymentRequestButtonElement is an
    // iframe-rendered button that internally re-checks device wallet
    // availability and crashes the React tree when our canMakePayment
    // override lies on a device that can't actually do AP/GP. When the
    // test harness sets window.__MIXTILES_FAKE_PAYMENT_BUTTON__, we swap
    // that component for a plain button so handleClick still runs through
    // the normal validate -> show path. Gated on non-production env so
    // the flag has no effect in a production bundle.
    const useFakeButton =
      window?.KEYS?.runtimeEnv !== 'production' &&
      window?.__MIXTILES_FAKE_PAYMENT_BUTTON__ === true

    const button = useFakeButton ? (
      <button
        type="button"
        data-testid="payment-request-button-stub"
        onClick={(e) => {
          e.preventDefault()
          this.handleClick()
        }}
        style={{
          height: '52px',
          borderRadius: '8px',
          width: '100%',
          background: '#000',
          color: '#fff',
          border: 'none',
        }}
      >
        Pay
      </button>
    ) : (
      <PaymentRequestButtonElement
        className="PaymentRequestButton"
        onClick={(e) => {
          e.preventDefault()
          this.handleClick()
        }}
        options={{
          paymentRequest: this.state.paymentRequest,
          style: {
            paymentRequestButton: {
              type: 'default',
              theme: 'dark',
              height: '52px',
              borderRadius: '8px',
            },
          },
        }}
      />
    )
    return <div>{button}</div>
  }
}

export default StripePaymentRequestForm
